import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

export interface APIResource {
  name: string;
  mimeType: string;
  data: any;
}

export enum LinkTarget {
  Self = '_self',
  Blank = '_blank',
  Parent = '_parent',
  Top = '_top',
}

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  constructor(private http: HttpClient) {}

  public downloadFile(filename: string, content: Blob, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }

  public openFile(
    content: Blob,
    target: LinkTarget = LinkTarget.Blank,
    fileName: string | null = null,
  ): void {
    // for IE
    // @ts-ignore: msSaveOrOpenBlob is only available in IE
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      // @ts-ignore: msSaveOrOpenBlob is only available in IE
      (window.navigator as any).msSaveOrOpenBlob(content, fileName);
    }
    // for Non-IE (chrome, firefox etc.)
    else {
      const blobUrl = URL.createObjectURL(content);
      window.open(blobUrl, target);
    }
  }

  public getAPIResource(relativeUrl: string): Observable<APIResource> {
    return this.http.get(relativeUrl, { observe: 'response', responseType: 'blob' }).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let resourceName = 'unknown';
        if (contentDisposition) {
          const matches = /filename="([^"]*)"/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            resourceName = matches[1];
          }
        }
        const mimeType = response.headers.get('Content-Type') || 'application/octet-stream';
        return {
          name: resourceName,
          mimeType: mimeType,
          data: response.body,
        };
      }),
    );
  }

  public downloadAPIResource(relativeUrl: string, target: LinkTarget = LinkTarget.Self): void {
    this.getAPIResource(relativeUrl).subscribe((resource) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(resource.data);
      link.download = resource.name || 'download';
      link.target = target;
      link.click();
    });
  }

  public openAPIResource(relativeUrl: string, target: LinkTarget = LinkTarget.Blank): void {
    this.getAPIResource(relativeUrl).subscribe((resource) => {
      const blobUrl = URL.createObjectURL(resource.data);
      const newWindow = window.open(blobUrl, target);
      if (newWindow) {
        newWindow.onload = () => {
          URL.revokeObjectURL(blobUrl);
        };
      }
    });
  }
}
